/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ProductCard } from './components/Product/ProductCard';
import { ProductViewer } from './components/Product/ProductViewer';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams, useSearchParams, Navigate } from 'react-router-dom';
import './App.css';
import { OperatorConsole } from './modules/operator/OperatorConsole';
import { UploadConsole } from './modules/upload/UploadConsole';

import DevicesAdminView from './components/Admin/DevicesAdminView';   // STAB-01 — módulo permanente de dispositivos
import EnrollPage from './components/Admin/EnrollPage';             // MP-STAB-04 — canje público de Device Enrollment
import socketClient from './services/socketClient';
import socketOperator from './services/socketOperator';
import { applyProductEvent, isProductVisible, createFetchGuard } from './services/realtimeContract';
import * as api from './services/api';
import * as deviceStore from './services/deviceStore';   // T3 — capa desacoplada de credenciales
import { initVersionAwareness } from './platform/versionAwareness';
import { VersionBanner } from './platform/VersionBanner';
import { busyRegistry } from './platform/busyRegistry';

// UX-001E: mismo formulario en ambos modos — /admin/upload (Create) y
// /admin/upload?edit=<code> (Edit Mode: valores precargados, submit PATCH).
// MODULE SCOPE (identidad estable): definido dentro de AppContent, cada
// re-render de App (p.ej. el evento realtime del propio PATCH → setProducts)
// crearía una función NUEVA → React remontaría el formulario y la pantalla
// de éxito se perdería. El callback se pasa por props.
// PLATFORM-UX-001: detección silenciosa de versión nueva (hash vs asset-manifest.json)
initVersionAwareness();

const UploadRoute = ({ onUpdated }: { onUpdated?: () => void }) => {
  const [params] = useSearchParams();
  return <UploadConsole onProductsUpdated={onUpdated} editCode={params.get('edit')} />;
};


// ═══════════════════════════════════════════════════════════════════════
// KF25-UX-001D — Working Context Continuity (2026-08-05)
// Estado de vista persistido en sessionStorage: sobrevive al remount por
// navegación operativa (PUSH) sin tocar backend / realtime / data model.
// Alcance: por pestaña. Se limpia SOLO en Cerrar sesión (clearViewContext).
// ═══════════════════════════════════════════════════════════════════════
const useViewMemory = (key: string, initial: any): [any, any] => {
  const memKey = 'kf25_vm_' + key;
  // ref-mirror del estado: permite calcular el siguiente valor SINCRONAMENTE en
  // el setter (antes de encolar el render) — la navegación del mismo batch
  // puede desmontar la vista y DESCARTAR el updater de React (race UX-001D:
  // handleBlock → setBlockedSearchProducts + navigate). La persistencia no
  // depende de efectos ni del ciclo de render: se escribe en el momento de la
  // llamada. Cubre todos los callers actuales y futuros con el mismo patrón.
  const stateRef = useRef<any>(initial);
  const [state, setState] = useState(() => {
    let v = initial;
    try {
      const raw = sessionStorage.getItem(memKey);
      if (raw !== null) v = JSON.parse(raw);
    } catch {}
    stateRef.current = v;
    return v;
  });
  const setPersisted = useCallback((v: any) => {
    const next = typeof v === 'function' ? v(stateRef.current) : v;
    stateRef.current = next;
    try { sessionStorage.setItem(memKey, JSON.stringify(next)); } catch {}
    setState(next);
  }, [memKey]);
  return [state, setPersisted];
};

// Restaura el scroll SOLO cuando la vista filtrada ya está reconstruida
// (ready = grid/resultados renderizados). Guardado CONTINUO de eventos
// TRUSTED (scroll del usuario): los eventos programáticos y los de clamp del
// navegador (isTrusted=false — ocurren al colapsar el contenido durante el
// cambio de ruta, ANTES de que el cleanup quite el listener) se ignoran.
const useRestoreScroll = (key: string, ready: boolean) => {
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!ready || restoredRef.current) return;
    restoredRef.current = true;
    const y = Number(sessionStorage.getItem('kf25_scroll_' + key) || 0);
    if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
  }, [ready]);
  useEffect(() => {
    let raf = 0;
    const save = (e?: Event) => {
      if (e && !e.isTrusted) return; // ignorar programáticos/clamp
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try { sessionStorage.setItem('kf25_scroll_' + key, String(window.scrollY)); } catch {}
      });
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => {
      window.removeEventListener('scroll', save);
      cancelAnimationFrame(raf);
    };
  }, []);
};

const clearViewContext = () => {
  try {
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('kf25_vm_') || k.startsWith('kf25_scroll_'))
      .forEach(k => sessionStorage.removeItem(k));
  } catch {}
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [cartId, setCartId] = useState(null as any);
  // KF25-AUTH-01/02 (RAS): la AUTORIDAD de acceso es el backend (Device Enrollment).
  // authority.state es el estado DERIVADO de la resolución — NUNCA se persiste,
  // NUNCA se lee de localStorage como fuente de verdad. operatorMode (abajo) es un
  // selector puro derivado de authority.state.
  const [authority, setAuthority] = useState({ state: 'resolving', reason: null } as any);
  const [operatorToken, setOperatorToken] = useState(() => localStorage.getItem('kf25_operator_token'));
  const operatorMode = authority.state === 'authorized';   // selector puro — derivado, no fuente
  // Sesión bootstrap (admin-session = contraseña): autoriza SOLO la superficie de
  // re-registro (/admin/devices) — NUNCA la consola operativa (H1 + Policy Engine).
  const isBootstrapSession = authority.state === 'authorized' && (authority as any).source === 'admin-session';
  const [loginError, setLoginError] = useState('');
  const [operatorActiveCart, setOperatorActiveCart] = useState<string | null>(() => {
    const saved = localStorage.getItem('kf25_operator_active_cart');
    return saved && operatorMode ? saved : null;
  });

  // Persist operatorActiveCart
  useEffect(() => {
    if (operatorActiveCart) {
      localStorage.setItem('kf25_operator_active_cart', operatorActiveCart);
    } else {
      localStorage.removeItem('kf25_operator_active_cart');
    }
  }, [operatorActiveCart]);
  const [cartItems, setCartItems] = useState([]);
  const [cartStatus, setCartStatus] = useState(null as any);
  const [cartPhase, setCartPhase] = useState<string | null>(null);
  const [cartCount, setCartCount] = useState(0);
  const [cartPulse, setCartPulse] = useState(false);
  const [blockedGlobal, setBlockedGlobal] = useState([]);
  // KF25-UX-001A: estado adminNavBar eliminado (barra púrpura histórica removida).

  // ═══ KF25-AUTH-01/02 (RAS) — RESOLVER DE AUTORIDAD ÚNICO ═══
  // La autoridad de acceso es SIEMPRE el backend (Device Enrollment). El frontend
  // NUNCA decide: consulta y representa. Este resolver corre en el arranque de la
  // aplicación y tras cada expulsión de sesión (evento kf25:session-expired).
  //
  // Flujo (agnóstico de quién es el usuario y de cuántos dispositivos existen):
  //   1. ¿Sesión device-status ya activa?  → sessionStatus(token) → authorized
  //   2. ¿No? → ¿Existe dispositivo registrado (deviceStore)? → deviceLogin
  //        → OK → authorized (nuevo token dev_token_*)
  //        → error tipado del backend → unauthorized(reason) — el backend comunica
  //          la causa: DEVICE_NOT_FOUND | DEVICE_REVOKED | DEVICE_REPLACED |
  //          DEVICE_INVALID_CREDENTIAL | DEVICE_TEMP_BLOCKED
  //   3. ¿No hay credenciales de dispositivo? → unauthorized(no_device)
  // El formulario de contraseña NUNCA aparece automáticamente (solo /admin/recovery).
  const resolveAuthority = useCallback(async () => {
    let cancelled = false;
    // (1) ¿Sesión ya activa? — consultar al backend, nunca confiar en estado local
    const existingToken = localStorage.getItem('kf25_operator_token');
    if (existingToken) {
      const st = await api.sessionStatus(existingToken);
      if (cancelled) return;
      if (st && st.status === 'active' && st.session && st.session.source === 'device-status') {
        setAuthority({ state: 'authorized', reason: null, source: 'device-status' });
        setOperatorToken(existingToken);
        return;
      }
      // KF25-AUTH-01/02 + MR-02 (Policy Engine v3): sesión admin-session (contraseña)
      // = SOLO bootstrap/re-registro. Autoriza la superficie de dispositivos
      // (DevicesAdminView), NUNCA la consola operativa (H1: socket exige
      // device-status; el Policy Engine además restringe device:manage a
      // device-status). El frontend solo REPRESENTA la decisión del backend.
      if (st && st.status === 'active' && st.session && st.session.source === 'admin-session') {
        // Si además existe un dispositivo registrado, la autoridad device-status
        // (RAS) PREVALECE sobre el bootstrap: tras redimir en /admin/recovery el
        // token de contraseña queda obsoleto y el usuario debe llegar a la consola.
        // Si el deviceLogin falla (credenciales obsoletas/revocadas), permanecemos
        // en bootstrap — la recuperación sigue siendo posible.
        const dev = deviceStore.get();
        if (dev && dev.deviceId && dev.deviceSecret) {
          try {
            const res = await api.deviceLogin(dev.deviceId, dev.deviceSecret);
            if (cancelled) return;
            if (res && res.success && res.token) {
              setOperatorToken(res.token);
              localStorage.setItem('kf25_operator_token', res.token);
              sessionStorage.setItem('kf25_op_token', res.token);
              if (res.adminId) localStorage.setItem('kf25_admin_id', res.adminId);
              setAuthority({ state: 'authorized', reason: null, source: 'device-status' });
              return;
            }
          } catch (e) { /* credenciales inválidas → seguir en bootstrap */ }
        }
        setAuthority({ state: 'authorized', reason: null, source: 'admin-session' });
        setOperatorToken(existingToken);
        return;
      }
      // Sesión no válida (muerta por restart/expiración/revocación) → resolver desde device
      api.handleSessionExpired(st && st.status ? st.status : 'not_found');
      // handleSessionExpired dispara el evento → este mismo resolver corre de nuevo
      // con el estado limpio; no continuamos aquí para evitar doble resolución.
      return;
    }
    // (2) ¿Existe dispositivo registrado? → device auth (la ÚNICA autoridad)
    const dev = deviceStore.get();
    if (!dev || !dev.deviceId || !dev.deviceSecret) {
      setAuthority({ state: 'unauthorized', reason: 'no_device' });
      return;
    }
    try {
      const res = await api.deviceLogin(dev.deviceId, dev.deviceSecret);
      if (cancelled) return;
      if (res && res.success && res.token) {
        setOperatorToken(res.token);
        localStorage.setItem('kf25_operator_token', res.token);
        sessionStorage.setItem('kf25_op_token', res.token);   // ← mismo key que lee AdminCartView (socket)
        // KF25-AUTH-01/02: la identidad de la sesión device-status la fija el
        // DISPOSITIVO (res.adminId) — se refleja localmente como metadata de
        // identidad (nunca como autoridad). Evita el ritual legacy de
        // "seleccionar administrador" (POST /admin/session) en flujo RAS.
        if (res.adminId) localStorage.setItem('kf25_admin_id', res.adminId);
        setAuthority({ state: 'authorized', reason: null });
      } else {
        setAuthority({ state: 'unauthorized', reason: 'invalid' });
      }
    } catch (err) {
      if (cancelled) return;
      const code = err && err.code;
      const reason = code === 'DEVICE_NOT_FOUND' ? 'no_device'
        : code === 'DEVICE_REVOKED' ? 'revoked'
        : code === 'DEVICE_REPLACED' ? 'replaced'
        : code === 'DEVICE_INVALID_CREDENTIAL' ? 'invalid'
        : code === 'DEVICE_TEMP_BLOCKED' ? 'blocked'
        : 'unknown';
      setAuthority({ state: 'unauthorized', reason });
    }
    return () => { cancelled = true; };
  }, []);

  // Resolver de autoridad — corre en el arranque de la aplicación (cada carga).
  useEffect(() => {
    resolveAuthority();
  }, [resolveAuthority]);

  // MP-STAB-03 — CHEQUEO PROACTIVO DE SESIÓN (contrato único /session/status).
  // Si la UI cree haber sesión (operatorMode) pero el backend ya no la reconoce
  // (reinicio, revocación, expiración), se delega en la rutina global.
  // El contrato describe el estado REAL: operator | admin-session | device-status.
  useEffect(() => {
    if (!operatorMode) return;
    const token = localStorage.getItem('kf25_operator_token');
    if (!token) return;
    let cancelled = false;
    api.sessionStatus(token).then(result => {
      if (cancelled || !result) return;   // error de red → no expulsar (spec §4.3)
      if (result.status !== 'active') {
        api.handleSessionExpired(result.status);
      }
    });
    return () => { cancelled = true; };
  }, [operatorMode]);

  // MP-STAB-03 + KF25-AUTH-01/02 — listener del evento global de sesión expirada.
  // La rutina global (api.handleSessionExpired) NO navega: limpia + informa.
  // Aquí la capa superior RE-RESUELVE la autoridad: si el dispositivo sigue
  // autorizado (registry + authz en disco), el Resolver re-autentica automáticamente
  // y el guard vuelve a la Consola — sin pantalla de contraseña. Si el dispositivo
  // fue revocado/reemplazado, el Resolver muestra el flujo de recuperación.
  useEffect(() => {
    const onExpired = (e: Event) => {
      const status = (e as CustomEvent).detail?.status || 'reauth_required';
      console.warn('[Session] Sesión no válida (' + status + ') — re-resolviendo autoridad de dispositivo');
      resolveAuthority();
    };
    window.addEventListener('kf25:session-expired', onExpired);
    return () => window.removeEventListener('kf25:session-expired', onExpired);
  }, [resolveAuthority]);

  // KF25-AUTH-01/02 (C1) — RE-RESOLUCIÓN AL FINALIZAR UN ENROLLMENT.
  // EnrollPage emite kf25:device-registered tras guardar las credenciales del
  // dispositivo. El Resolver vuelve a CONSULTAR al backend de inmediato — la UI
  // refleja la nueva autoridad sin esperar navegación ni recarga. La autoridad
  // sigue siendo exclusivamente el backend; esto solo representa su respuesta.
  useEffect(() => {
    const onRegistered = () => resolveAuthority();
    window.addEventListener('kf25:device-registered', onRegistered);
    return () => window.removeEventListener('kf25:device-registered', onRegistered);
  }, [resolveAuthority]);

  // KF25-AUTH-01/02 (C1) — RE-RESOLUCIÓN AL ENTRAR A RUTAS PROTEGIDAS (/admin*).
  // El Resolver también corre al navegar por SPA (sin recarga) hacia la Consola:
  // tras completar un Enrollment, entrar a /admin refleja la autoridad real en
  // lugar de un estado obsoleto. No introduce flags persistidos; únicamente
  // vuelve a consultar al backend (sessionStatus/deviceLogin). Se excluye
  // /admin/recovery (flujo explícito de bootstrap — no es ruta protegida).
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const curr = location.pathname;
    const isProtected = curr.startsWith('/admin') && !curr.startsWith('/admin/recovery');
    const wasProtected = prevPathRef.current.startsWith('/admin') && !prevPathRef.current.startsWith('/admin/recovery');
    prevPathRef.current = curr;
    if (isProtected && !wasProtected) {
      resolveAuthority();
    }
  }, [location.pathname, resolveAuthority]);

  // KF25-AUTH-01/02: la contraseña NO establece autoridad de acceso a la Consola.
  // Solo obtiene el token de operador para el flujo EXPLÍCITO de bootstrap/
  // recuperación (/admin/recovery) — crear enrollments para re-registrar
  // dispositivos. La autoridad de la Consola es SIEMPRE el Device Enrollment.
  const handleLogin = async (password) => {
    try {
      const res = await api.operatorLogin(password);
      if (res && res.success) {
        setOperatorToken(res.token);
        setLoginError('');
        localStorage.setItem('kf25_operator_token', res.token);
      } else {
        setLoginError('Contraseña incorrecta');
      }
    } catch (err) {
      setLoginError('Error al conectar con el servidor');
    }
  };
  const handleLogout = () => {
    if (!window.confirm('¿Cerrar sesión?')) return;
    setAuthority({ state: 'unauthorized', reason: 'no_device' });
    setOperatorToken(null);
    setLoginError('');
    setOperatorActiveCart(null);
    localStorage.removeItem('kf25_operator_token');
    clearViewContext(); // UX-001D: el contexto de trabajo se limpia SOLO aquí (logout)
    navigate('/');
  };

  // Initialize
  useEffect(() => {
    socketClient.connect();

    // KF25-UX-001B F2: el fetch de montaje usa el MISMO contrato + guard que
    // refreshProducts (un evento recibido antes de la respuesta del fetch ya
    // no se pierde por el replace).
    const mountFetch = productsFetchGuard.current.next();
    api.listProducts()
      .then(data => {
        if (mountFetch.isCurrent() && data.success && data.products) applyFetchedProducts(data.products);
      })
      .catch(() => {});

    api.getBlockedProducts()
      .then(data => {
        if (data.success && data.blocked) setBlockedGlobal(data.blocked.map(function(b: any) { return b.productCode; }));
      })
      .catch(() => {});

    const savedCartId = localStorage.getItem('kf25_current_cart_id');

    if (savedCartId) {
      setCartId(savedCartId);
  
      api.getCart(savedCartId)
        .then(data => {
          if (data.success && data.cart) {
            const c = data.cart;
        
        
            if (c.closed || (c.timer && c.timer.phase === 'expired')) {
          
              localStorage.removeItem('kf25_current_cart_id');
              setCartId(null);
              setCartItems([]);
              setCartCount(0);
            } else {
              setCartItems(c.items || []);
              setCartStatus(c.status);
              setCartPhase(c.timer?.phase || null);
              setCartCount((c.items || []).length);
            }
          }
        })
        .catch(() => {
      
          localStorage.removeItem('kf25_current_cart_id');
          setCartId(null);
          setCartItems([]);
          setCartCount(0);
        });
    }

    // KF25-UX-001A: barra púrpura histórica ELIMINADA (navegación redundante).
    // El operador navega desde su consola ([Tienda →]) y vuelve ([← Panel]);
    // la tienda pública queda 100% de cliente. kf25_admin_last_cart ya no se
    // lee para navegación (el write en AdminCartView queda como dato inerte).

    // Socket event handlers — Realtime Platform (eventos canónicos deterministas)
    socketClient.on('item:sold', (data) => { if (data.cartId) refreshCart(data.cartId); });
    socketClient.on('item:released', (data) => { if (data.cartId) refreshCart(data.cartId); });
    socketClient.on('payment:verifying', (data) => { if (data.cartId) refreshCart(data.cartId); });
    socketClient.on('item:removed', (data) => { if (data.cartId) refreshCart(data.cartId); });
    socketClient.on('item:added', (data) => { if (data.cartId) refreshCart(data.cartId); });
    // product:changed — aplicación DETERMINISTA (contrato canónico, sin refetch).
    // Protección stale/out-of-order por versión en applyProductEvent.
    socketClient.on('product:changed', (data) => {
      setProducts(prev => applyProductEvent(prev, data));
    });

    socketClient.on('timer:expired', (data) => {
      if (data.cartId) {
        const currentCartId = localStorage.getItem('kf25_current_cart_id');
        if (data.cartId === currentCartId) {
          localStorage.removeItem('kf25_current_cart_id');
          setCartId(null); setCartItems([]); setCartCount(0);
        }
      }
    });

    // Resync automático tras RECONEXIÓN (recupera eventos perdidos — Fase 5)
    socketClient.onResync(() => {
      refreshProducts();
      api.getBlockedProducts()
        .then(data => {
          if (data.success && data.blocked) setBlockedGlobal(data.blocked.map(function(b: any) { return b.productCode; }));
        })
        .catch(() => {});
      const savedCartId = localStorage.getItem('kf25_current_cart_id');
      if (savedCartId) {
        if (socketClient.connected) {
          socketClient.requestSync([savedCartId]);
        } else {
          api.getCart(savedCartId).then(data => {
            if (data.success && data.cart) {
              setCartItems(data.cart.items || []);
              setCartCount((data.cart.items || []).length);
              setCartStatus(data.cart.status);
              setCartPhase(data.cart.timer?.phase || null);
              setCartId(data.cart.id);
            }
          }).catch(() => {});
        }
      }
    });

    socketClient.on('reconnect:sync', (data) => {
      if (data.cart) {
        if (data.cart.closed || (data.cart.timer && data.cart.timer.phase === 'expired')) {
          localStorage.removeItem('kf25_current_cart_id');
          setCartId(null);
          setCartItems([]);
          setCartCount(0);
        } else {
          setCartItems(data.cart.items || []);
          setCartCount((data.cart.items || []).length);
          setCartStatus(data.cart.status);
          setCartPhase(data.cart.timer?.phase || null);
          setCartId(data.cart.id);
        }
      }
    });

    // Re-sync on return from background
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshProducts();
        api.getBlockedProducts()
          .then(data => {
            if (data.success && data.blocked) setBlockedGlobal(data.blocked.map(function(b: any) { return b.productCode; }));
          })
          .catch(() => {});
        const savedCartId = localStorage.getItem('kf25_current_cart_id');
        if (savedCartId) {
          if (socketClient.connected) {
            socketClient.requestSync([savedCartId]);
          } else {
            api.getCart(savedCartId).then(data => {
              if (data.success && data.cart) {
                if (data.cart.closed || (data.cart.timer && data.cart.timer.phase === 'expired')) {
                  localStorage.removeItem('kf25_current_cart_id');
                  setCartId(null);
                  setCartItems([]);
                  setCartCount(0);
                }
              }
            }).catch(() => {});
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      socketClient.disconnect();
    };
  }, []);

  // KF25-UX-001B F2: guard de secuencia compartido + merge por contrato.
  // Solo la respuesta del fetch MÁS RECIENTE se aplica (nunca una stale
  // reemplaza un estado más nuevo), y el merge conserva lo que los eventos
  // realtime ya habían aplicado.
  const productsFetchGuard = useRef(createFetchGuard());
  const applyFetchedProducts = useCallback((fetched) => {
    setProducts(prev => (fetched || []).reduce((acc, p) =>
      applyProductEvent(acc, { entityId: p.code, current: p, version: p.updatedAt }), prev));
  }, []);

  // Re-fetch products after admin action — passed to OperatorConsole
  const refreshProducts = useCallback(() => {
    // KF25-UX-001B F2: la respuesta del fetch se aplica a través del MISMO
    // contrato determinista que los eventos realtime (merge con guard de
    // versión), en lugar del replace crudo — un refetch en vuelo durante una
    // creación ya no borra el producto añadido por el evento.
    const g = productsFetchGuard.current.next();
    api.listProducts().then(r => {
      if (g.isCurrent() && r && r.success) applyFetchedProducts(r.products);
    });
  }, [applyFetchedProducts]);

  // ============================================
  // ACTIVITY PING — Inactivity timer reset
  // Sends to backend every interaction to reset the 8-minute timer
  // ============================================
  const [activityTimer, setActivityTimer] = useState(null as any);
  const pingActivity = useCallback(() => {
    const currentCartId = cartId || localStorage.getItem('kf25_current_cart_id');
    if (!currentCartId) return;
    // Debounce: only send every 30 seconds max
    if (activityTimer) return;
    const t = setTimeout(() => setActivityTimer(null), 30000);
    setActivityTimer(t);
    // Use the cart PATCH endpoint to signal activity (no data changes, updates lastActivity)
    fetch('/api/carts/' + currentCartId, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ _activity: true })
    }).catch(() => {});
  }, [cartId, activityTimer]);

  // Set up global activity listeners
  useEffect(() => {
    const handler = () => pingActivity();
    window.addEventListener('click', handler);
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('keydown', handler);
    window.addEventListener('touchstart', handler, { passive: true });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('scroll', handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
      if (activityTimer) clearTimeout(activityTimer);
    };
  }, [pingActivity, activityTimer]);

  const refreshCart = useCallback((targetCartId) => {
    const currentCartId = cartId || localStorage.getItem('kf25_current_cart_id');
    const effectiveCartId = targetCartId || currentCartId;
    if (!effectiveCartId) return;
    api.getCart(effectiveCartId)
      .then(data => {
        if (data.success && data.cart) {
          setCartItems(data.cart.items || []);
          setCartCount((data.cart.items || []).length);
          setCartStatus(data.cart.status);
          setCartPhase(data.cart.timer?.phase || null);
        }
      })
      .catch(() => {});
  }, [cartId]);

  async function addToCart(productCode, productName, productPrice) {


    try {
      let currentCartId = cartId || localStorage.getItem('kf25_current_cart_id');
  
      if (!currentCartId) {
    
        const data = await api.createCart([{ productCode, name: productName, price: productPrice }]);
        if (!data.success) {return false; }
        currentCartId = data.cart_id;
    
        setCartId(currentCartId);
        localStorage.setItem('kf25_current_cart_id', currentCartId);
        setCartItems(data.cart.items || []);
        setCartCount(1);
        setCartPulse(true);
        setTimeout(() => setCartPulse(false), 500);
        socketClient.joinCart(currentCartId);
        return true;
      } else {
    
        const data = await api.addItemToCart(currentCartId, productCode, productName, productPrice);
        if (!data.success) { return false; }
    
        setCartItems(data.cart.items || []);
        setCartCount((data.cart.items || []).length);
        setCartPulse(true);
        setTimeout(() => setCartPulse(false), 500);
        return true;
      }
    } catch (err) {
      
      return false;
    }
  };

  const removeFromCart = async (productCode) => {
    const currentCartId = cartId || localStorage.getItem('kf25_current_cart_id');

    if (!currentCartId) {return false; }
    try {
      const data = await api.removeItemFromCart(currentCartId, productCode);
  
      if (data.success) {
        setCartItems(data.cart.items || []);
        const newCount = (data.cart.items || []).length;
        setCartCount(newCount);
        if (newCount === 0) {
          localStorage.removeItem('kf25_current_cart_id');
          setCartId(null);
        }
        return true;
      }
    } catch (err) {
      console.error('[Cart] Error removing:', err.message);
    }
    return false;
  };

  // ============================================
  // RECOVERY LOGIN (KF25-AUTH-01/02) — SOLO bootstrap/recuperación explícita.
  // La contraseña NO da acceso a la Consola (autoridad = Device Enrollment).
  // Esta pantalla se muestra ÚNICAMENTE en /admin/recovery — nunca automáticamente.
  // Sirve para crear enrollments (re-registrar dispositivos) y flujos de
  // recuperación de futuros operadores. Tras autenticar, enlaza a /admin/devices.
  const RecoveryLogin = () => {
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // Bootstrap v3 (MR-01/MR-02): paso 1 = contraseña (identidad) → paso 2 =
    // seleccionar administrador + POST /admin/session (establece la admin-session).
    // La contraseña NUNCA da consola — solo la superficie de re-registro.
    const [step, setStep] = useState<'password' | 'select-admin'>('password');
    const [admins, setAdmins] = useState<any[]>([]);
    const [selectError, setSelectError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      // Autenticación DIRECTA (sin handleLogin): handleLogin hace setOperatorToken,
      // lo que re-renderiza App y REMONTA este componente (definido inline) → se
      // perdería el estado del picker. Aquí solo se persiste el token; el Resolver
      // lo leerá de localStorage al navegar (tras establecer la admin-session).
      try {
        const res = await api.operatorLogin(password);
        if (res && res.success) {
          localStorage.setItem('kf25_operator_token', res.token);
          const data = await api.requestAuth('GET', '/api/auth/admins', null, res.token);
          setAdmins(data.admins || []);
          setStep('select-admin');
        } else {
          setLoginError('Contraseña incorrecta');
        }
      } catch {
        setLoginError('No se pudo iniciar sesión de recuperación');
      } finally {
        setSubmitting(false);
      }
    };

    const handleSelectAdmin = async (adminId: string) => {
      setSelectError('');
      setSubmitting(true);
      try {
        const opToken = localStorage.getItem('kf25_operator_token') || '';
        const res = await api.requestAuth('POST', '/api/auth/admin/session', { token: opToken, adminId }, opToken);
        if (res && res.success) {
          localStorage.setItem('kf25_admin_id', adminId);
          navigate('/admin/devices');
        } else {
          setSelectError('No se pudo establecer la sesión de recuperación');
        }
      } catch {
        setSelectError('No se pudo establecer la sesión de recuperación');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm border border-purple-100">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Recuperación de acceso</h1>
            <p className="text-sm text-gray-500 mt-1">KimberlyFashion25 — solo para re-registrar dispositivos</p>
          </div>
          {step === 'password' ? (
            <form onSubmit={handleSubmit}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña de operador</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingrese la contraseña"
                autoFocus
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-colors"
              />
              {loginError && <p className="text-sm text-red-600 mt-2">{loginError}</p>}
              <button
                type="submit"
                disabled={submitting || !password}
                className="w-full mt-4 px-4 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Ingresando...' : 'Continuar'}
              </button>
            </form>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-3">Selecciona tu administrador para re-registrar el dispositivo:</p>
              {admins.map((a) => (
                <button
                  key={a.id}
                  onClick={() => handleSelectAdmin(a.id)}
                  disabled={submitting}
                  className="w-full mb-2 px-4 py-2.5 bg-purple-50 border border-purple-200 text-purple-800 font-medium rounded-lg hover:bg-purple-100 disabled:bg-gray-100 disabled:text-gray-400 transition-colors text-left"
                >
                  {a.name} <span className="text-xs text-gray-400">{a.id} · {a.role}</span>
                </button>
              ))}
              {selectError && <p className="text-sm text-red-600 mt-2">{selectError}</p>}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ═══ KF25-AUTH-01/02 — PANTALLA DE AUTORIDAD DE DISPOSITIVO ═══
  // Se muestra cuando el Resolver determinó unauthorized. NUNCA muestra el
  // formulario de contraseña automáticamente — solo el flujo de recuperación
  // correspondiente a la razón que el BACKEND comunicó (el frontend no infiere).
  // Las razones vienen tipadas del backend: no_device | revoked | replaced |
  // invalid | blocked | unknown.
  const DeviceAuthScreen = ({ reason }: { reason: string | null }) => {
    const messages: Record<string, { title: string; desc: string; action: string }> = {
      no_device: {
        title: 'Dispositivo no registrado',
        desc: 'Este dispositivo no está autorizado para acceder a la Consola Operativa. Usa un Enrollment Link para registrarlo.',
        action: 'Registrar este dispositivo',
      },
      revoked: {
        title: 'Dispositivo revocado',
        desc: 'Este dispositivo fue revocado y ya no está autorizado. Debes registrar un dispositivo nuevo para acceder.',
        action: 'Registrar dispositivo nuevo',
      },
      replaced: {
        title: 'Dispositivo reemplazado',
        desc: 'Este dispositivo fue reemplazado por uno nuevo. Usa el dispositivo nuevo o registra este de nuevo.',
        action: 'Registrar dispositivo nuevo',
      },
      invalid: {
        title: 'Credencial de dispositivo inválida',
        desc: 'Las credenciales de este dispositivo ya no son válidas. Regístralo de nuevo con un Enrollment Link.',
        action: 'Registrar dispositivo',
      },
      blocked: {
        title: 'Demasiados intentos fallidos',
        desc: 'Este dispositivo fue bloqueado temporalmente por intentos fallidos. Espera unos minutos e inténtalo de nuevo.',
        action: 'Reintentar ahora',
      },
      unknown: {
        title: 'Acceso no autorizado',
        desc: 'No se pudo verificar la autorización de este dispositivo. Inténtalo de nuevo o usa el flujo de recuperación.',
        action: 'Reintentar',
      },
    };
    const m = messages[reason || 'unknown'] || messages.unknown;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md border border-purple-100 text-center">
          <div className="text-5xl mb-4">📱</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{m.title}</h1>
          <p className="text-sm text-gray-600 mb-6">{m.desc}</p>
          <div className="space-y-3">
            <button
              onClick={() => resolveAuthority()}
              className="w-full px-4 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              {m.action}
            </button>
            <button
              onClick={() => navigate('/admin/recovery')}
              className="w-full px-4 py-2.5 bg-white text-purple-700 font-medium rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors"
            >
              🔑 Recuperación de acceso (solo re-registro)
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ═══ KF25-AUTH-01/02 — CARGA DE AUTORIDAD ═══
  // Mientras el Resolver consulta al backend (resolving), no se muestra ni la
  // consola ni el login: solo una pantalla de verificación. El acceso NUNCA se
  // decide localmente — se espera la respuesta del backend.
  const AuthorityLoadingScreen = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="text-center">
        <div className="animate-spin w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-sm text-gray-600">Verificando dispositivo autorizado…</p>
      </div>
    </div>
  );

  // HOME VIEW
  // ============================================
  const HomeView = () => {
    // UX-001D: la búsqueda sobrevive a la navegación (detalle → volver)
    const [searchQuery, setSearchQuery] = useViewMemory('home.search', '');
    // UX-001D: scroll restaurado SOLO cuando el grid filtrado está renderizado
    useRestoreScroll('home', products.length > 0);
    const filteredProducts = products.filter(p => {
      // Realtime Platform: visibilidad determinista local (hidden se excluye
      // también client-side — los eventos product:changed pueden marcarlo).
      if (!isProductVisible(p)) return false;
      if (!searchQuery) return true;
      const raw = searchQuery.trim();

      // ── BÚSQUEDA NUMÉRICA EXACTA ──
      // "1" o "01" o "001" → solo KF25-001. 0 falsos positivos.
      if (/^\d+$/.test(raw)) {
        const padded = String(parseInt(raw, 10)).padStart(3, '0');
        return p.code === 'KF25-' + padded;
      }

      // ── BÚSQUEDA POR CÓDIGO COMPLETO ──
      const upper = raw.toUpperCase();
      if (upper.startsWith('KF25-')) {
        return p.code === upper;
      }

      // ── BÚSQUEDA TEXTUAL (fallback) ──
      const q = raw.toLowerCase();
      return (p.name || '').toLowerCase().includes(q) ||
             (p.category || '').toLowerCase().includes(q);
    });

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <img src="/logos/logo-kimberly-clean.png" alt="Kimberly Fashion"
            style={{ display: 'block', margin: '0 auto', maxWidth: '300px', height: 'auto' }} />
        </div>
        <p style={{ textAlign: 'center', fontSize: '20px', fontWeight: '600', color: '#8B2E5D', marginTop: '2px', marginBottom: '6px' }}>
          Te ves elegante sin gastar de más.
        </p>

        {/* 🔍 Búsqueda pública */}
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="🔍 Buscar... (ej: KF25-051, vestido, blusa)"
            className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-gray-700 transition-shadow"
          />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          {searchQuery ? 'Resultados de búsqueda (' + filteredProducts.length + ')' : 'Nuestra colección'}
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => {
            const isInCart = cartItems.some(item => item.productCode === product.code);
            const isBlocked = isInCart || blockedGlobal.includes(product.code) ||
              product.status === 'verifying' || product.status === 'sold' || product.manualBlockedBy ||
              !!product.currentCart;

            return (
              <ProductCard
                key={product.code}
                product={product}
                isInCart={isInCart}
                isBlocked={isBlocked}
                onOpen={(code) => { if (!isBlocked) navigate('/producto/' + code); else if (operatorMode) navigate('/producto/' + code); else if (isInCart) navigate('/carrito'); }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================
  // PRODUCT VIEW
  // ============================================
  const ProductView = ({ productCode }) => {
    // PRINCIPIO KF25-ENT-01 (2026-08-03): el detalle resuelve la ENTIDAD por código,
    // NUNCA busca en la colección (products[] es una proyección del catálogo).
    // Esto hace al detalle inmune a la desaparición del producto de la lista
    // (hidden/sold/archived) — la entidad se consulta directamente al backend.
    const [detailProduct, setDetailProduct] = useState(null);
    const [detailLoading, setDetailLoading] = useState(true);
    // UX-001E (fix ProductView): el MISMO modal de edición que AdminOpView —
    // sin navegación; refreshTick re-ejecuta la carga al cerrar.
    const [editOpen, setEditOpen] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
      let cancelled = false;
      setDetailLoading(true);
      api.getProduct(productCode, { includeHidden: operatorMode })
        .then(r => { if (!cancelled && r.success) setDetailProduct(r.product); })
        .catch(() => { if (!cancelled) setDetailProduct(null); })
        .finally(() => { if (!cancelled) setDetailLoading(false); });
      return () => { cancelled = true; };
    }, [productCode, operatorMode, refreshTick]);

    const product = detailProduct;
    const isInCart = product ? cartItems.some(item => item.productCode === product.code) : false;
    const isBlocked = product ? (isInCart || blockedGlobal.includes(product.code) || product.status === 'verifying' || product.status === 'sold' || product.manualBlockedBy || !!product.currentCart) : false;
    const [added, setAdded] = useState(isInCart);

    if (!product) {
      return (
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            {detailLoading ? (
              <div className="text-5xl mb-4 animate-pulse">⏳</div>
            ) : (
              <div className="text-5xl mb-4">❌</div>
            )}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{detailLoading ? 'Cargando producto...' : 'Producto no encontrado'}</h2>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
              ← Volver a la tienda
            </button>
          </div>
        </div>
      );
    }

    const handleAddToCart = async () => {
      // Si operador tiene carrito activo, agregar a ese carrito
      if (operatorMode && operatorActiveCart) {
        const res = await fetch('/api/carts/' + operatorActiveCart + '/items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productCode: product.code, name: product.name, price: product.price })
        });
        if (res.ok) {
          setAdded(true);
          alert('✅ Producto agregado a ' + operatorActiveCart);
        } else if (res.status === 409) {
          alert('⚠️ Este producto ya está en ' + operatorActiveCart);
        } else {
          alert('❌ No se pudo agregar al carrito. Verifica que el carrito existe.');
        }
        return;
      }
      // Flujo normal de cliente
      const success = await addToCart(product.code, product.name, product.price);
      if (success) setAdded(true);
    };

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 bg-gray-50">
              {/* Media Viewer — VCE (contrato: contenido + contexto + config) */}
              <ProductViewer product={product} />
            </div>
            <div className="p-8">
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <span className="bg-purple-200 text-purple-700 text-sm px-3 py-1.5 rounded">{product.category}</span>
                  <span className="font-mono text-lg text-gray-800 bg-gray-200 px-3 py-1.5 rounded">{product.code}</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-3">{product.name}</h1>
                <div className="text-4xl font-bold text-gray-900 mb-6">RD$ {(product.price || 0).toLocaleString()}</div>
                <p className="text-gray-700 mb-8">{product.description}</p>
              </div>

              {/* ── Barra operacional admin ── */}
              {operatorMode && (
                <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => navigate('/admin')}
                      style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                      ← Volver a consola
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm('¿Liberar ' + product.code + '?')) return;
                      const res = await api.updateProductStatus(product.code, 'active');
                      if (res.success) alert('✅ Producto liberado');
                    }}
                      style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                      🆓 Liberar
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm('¿Marcar ' + product.code + ' como verificación de pago?')) return;
                      const res = await api.updateProductStatus(product.code, 'verifying');
                      if (res.success) alert('✅ En verificación');
                    }}
                      style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                      🔍 Verificación de pago
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm('¿Marcar ' + product.code + ' como vendido?')) return;
                      const res = await api.updateProductStatus(product.code, 'sold');
                      if (res.success) alert('✅ Marcado como vendido');
                    }}
                      style={{ padding: '8px 16px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                      ✅ Vendido
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm('¿Ocultar ' + product.code + ' del feed público?')) return;
                      const res = await api.hideProduct(product.code);
                      if (res.success) alert('✅ Ocultado');
                    }}
                      style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                      ◐ Ocultar
                    </button>
                    {/* UX-001E (fix ProductView): ✏️ Editar en la barra operacional —
                        abre el MISMO modal de edición sobre esta vista (sin navegación). */}
                    <button onClick={() => setEditOpen(true)}
                      style={{ padding: '8px 16px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                      ✏️ Editar
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <button onClick={handleAddToCart} disabled={added || isBlocked}
                  className={'w-full py-4 px-6 rounded-lg font-bold text-lg transition-all duration-300 ' +
                    (added ? 'bg-green-600 text-white cursor-not-allowed opacity-90' :
                     isBlocked ? 'bg-gray-300 text-gray-500 cursor-not-allowed' :
                     'bg-purple-600 text-white hover:bg-purple-700')}>
                  {added ? '✔ Agregado al carrito' : isBlocked ? 'No disponible' : '🛒 Agregar al carrito'}
                </button>
                <button onClick={() => navigate('/carrito')} className="mt-2 w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-900 transition-colors">
                  Ir al carrito
                </button>
                <button onClick={() => navigate('/')} className="mt-2 w-full border border-gray-500 text-gray-900 py-2 rounded-lg bg-white hover:bg-gray-100 transition-colors">
                  Volver a la tienda
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* UX-001E modal (mismo que AdminOpView) sobre ProductView — sin navegación;
            al guardar (onSuccess) se cierra y refreshTick re-carga el producto:
            la info actualizada queda visible de inmediato en esta misma vista. */}
        {editOpen && product && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4"
            onClick={(e) => { if (e.target === e.currentTarget) setEditOpen(false); }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl relative">
              <button
                onClick={() => setEditOpen(false)}
                aria-label="Cerrar edición"
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 text-lg leading-none flex items-center justify-center transition-colors"
              >✕</button>
              <UploadConsole
                editCode={product.code}
                onProductsUpdated={refreshProducts}
                onSuccess={() => { setEditOpen(false); setRefreshTick(t => t + 1); }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // CART VIEW
  // ============================================
  const CartView = () => {
    const [cartItemsView, setCartItemsView] = useState([]);

    // Si es operador autenticado y hay cartId en URL, redirigir a consola operativa
    const queryCartId = new URLSearchParams(window.location.search).get('cart');
    useEffect(() => {
      if (operatorMode && queryCartId) {
        navigate('/admin/cart/' + queryCartId, { replace: true });
      }
    }, [operatorMode, queryCartId, navigate]);

    useEffect(() => {
      const items = cartItems.map(item => {
        const product = products.find(p => p.code === item.productCode);
        return { ...item, product: product || null };
      }).filter(item => item.product);
      setCartItemsView(items);
    }, [cartItems, products]);

    const totalItems = cartItemsView.length;
    const totalPrice = cartItemsView.reduce((sum, item) => sum + ((item.product?.price || item.price) || 0), 0);

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Tu carrito de compras</h1>
          <div className="text-gray-600">{totalItems === 0 ? 'Tu carrito está vacío' : totalItems + ' productos'}</div>
        </div>
        {totalItems === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-6">🛒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Tu carrito está vacío</h2>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Explorar productos</button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {cartItemsView.map((item) => (
                <div key={item.productCode} className="p-6">
                  <div className="flex">
                    <div className="w-32 h-32 mr-6 product-media">
                      <img src={item.product?.images?.[0]} alt={item.product?.name} className="w-full h-full rounded-lg" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">{item.product?.name || item.name}</h3>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="bg-purple-200 text-purple-700 text-xs px-2 py-1 rounded">{item.product?.category}</span>
                            <span className="text-sm font-mono text-gray-800 bg-gray-200 px-2 py-1 rounded">{item.productCode}</span>
                            <span className={'px-2 py-1 rounded-full text-xs font-medium ' +
                              (item.status === 'sold' ? 'bg-green-100 text-green-800' :
                               item.status === 'verifying' ? 'bg-blue-100 text-blue-800' :
                               item.status === 'active' ? 'bg-gray-100 text-gray-500' : 'bg-yellow-100 text-yellow-800')}>
                              {item.status === 'sold' ? 'Vendido' : item.status === 'verifying' ? 'En verificación de pago' : item.status === 'active' ? 'Liberado' : 'Pendiente'}
                            </span>
                          </div>
                          <div className="text-2xl font-bold text-gray-900">RD$ {((item.product?.price || item.price) || 0).toLocaleString()}</div>
                        </div>
                      </div>
                      <div className="mt-6 flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-700">Producto único (1 unidad)</div>
                        <button onClick={() => removeFromCart(item.productCode)}
                          disabled={item.status === 'sold' || item.status === 'verifying'}
                          className={'font-medium ' + (item.status === 'sold' || item.status === 'verifying' ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800')}>
                          {item.status === 'sold' || item.status === 'verifying' ? 'En proceso de compra' : 'Eliminar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-between text-lg font-bold mb-6">
                <span>Total</span>
                <span className="text-purple-600">RD$ {totalPrice.toLocaleString()}</span>
              </div>
              {cartPhase === 'checkout_sent' ? (
                <div className="w-full py-4 px-6 bg-green-50 border border-green-200 rounded-lg text-center">
                  <div className="font-medium text-green-800 mb-1">✅ Pedido enviado</div>
                  <div className="text-sm text-green-700">Ya contactamos al operador. Recibirás respuesta por WhatsApp.</div>
                </div>
              ) : (
                <button onClick={() => navigate('/checkout')} className="w-full py-4 px-6 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-colors">
                  Proceder al checkout
                </button>
              )}
              <button onClick={() => navigate('/')} className="mt-4 w-full px-4 py-2.5 bg-purple-100 text-purple-800 hover:bg-purple-200 font-medium rounded-lg border border-purple-300 transition-colors shadow-sm hover:shadow">
                ← Volver a la tienda
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // CHECKOUT VIEW
  // ============================================
  const CheckoutView = () => {
    const [cartItemsView, setCartItemsView] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [clientName, setClientName] = useState('');
    const [clientPhone, setClientPhone] = useState('');

    useEffect(() => {
      const items = cartItems.map(item => {
        const product = products.find(p => p.code === item.productCode);
        return { ...item, product };
      }).filter(item => item.product);
      setCartItemsView(items);
      setTotalPrice(items.reduce((sum, item) => sum + ((item.product?.price || item.price) || 0), 0));
    }, [cartItems, products]);

    const handleCheckout = async () => {
      const currentCartId = cartId || localStorage.getItem('kf25_current_cart_id');
      // PLATFORM-UX-001: checkout en vuelo = operación crítica → ocupado
      busyRegistry.acquire('checkout');
      try {
        if (!currentCartId) { alert('No hay carrito activo');return; }
        const data = await api.checkoutCart(currentCartId, clientName || '', clientPhone || '');
        if (data.code === 'CHECKOUT_ALREADY_DONE') {
          alert('Este pedido ya fue enviado al operador.');
          setCartPhase('checkout_sent');
          return;
        }
        if (data.success) {
          setCartPhase('checkout_sent');
          const message = 'Pedido ' + currentCartId + '\n\nhttp://kimberlyfashion25.com/cart/' + currentCartId;
          window.open('https://wa.me/18494055101?text=' + encodeURIComponent(message), '_blank');
        } else {
          alert('Error: ' + (data.error || 'Error desconocido'));
        }
      } catch (err) {
        alert('Error al procesar checkout. Intenta nuevamente.');
      } finally {
        busyRegistry.release('checkout');
      }
    };

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <button onClick={() => navigate('/carrito')} className="text-purple-600 hover:text-purple-800 font-medium mb-4">← Volver al carrito</button>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
          <div className="text-gray-600">{cartItemsView.length === 0 ? 'Tu carrito está vacío' : cartItemsView.length + ' productos en el carrito'}</div>
        </div>
        {cartItemsView.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-6">🛒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Tu carrito está vacío</h2>
            <p className="text-gray-700 mb-8">Agrega productos al carrito para poder iniciar el proceso de compra.</p>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Explorar productos</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Resumen del pedido</h2>
              <div className="space-y-4 mb-6">
                {cartItemsView.map((item) => (
                  <div key={item.productCode} className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center">
                      <div className="w-16 h-16 mr-4 product-media"><img src={item.product?.images?.[0]} alt={item.product?.name} className="w-full h-full rounded-lg" /></div>
                      <div><div className="font-medium text-gray-900">{item.product?.name || item.name}</div><div className="text-sm text-gray-600">{item.product?.category}</div></div>
                    </div>
                    <div className="text-lg font-bold text-gray-900">RD$ {((item.product?.price || item.price) || 0).toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between text-lg font-bold"><span>Total del pedido</span><span className="text-purple-600">RD$ {totalPrice.toLocaleString()}</span></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Tus datos</h2>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <div className="text-blue-600 mr-3">ℹ️</div>
                  <div>
                    <div className="font-medium text-blue-900 mb-1">Proceso de compra</div>
                    <div className="text-blue-700 text-sm">Ingresa tus datos y al hacer clic en "Iniciar proceso de compra", se abrirá WhatsApp para contactar al operador.</div>
                  </div>
                </div>
              </div>
              <div className="space-y-4 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                  <input type="text" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Ej: Ana López"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input type="tel" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="Ej: 8095550100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                </div>
              </div>
              <div className="space-y-4">
                {cartPhase === 'checkout_sent' ? (
                  <div className="w-full py-6 px-6 bg-green-50 border border-green-200 rounded-lg text-center">
                    <div className="text-2xl mb-2">✅</div>
                    <div className="font-medium text-green-800 mb-1">Pedido ya enviado</div>
                    <div className="text-sm text-green-700">Este pedido ya fue enviado al operador. Recibirás respuesta por WhatsApp.</div>
                  </div>
                ) : (
                  <button onClick={handleCheckout} className="w-full py-4 px-6 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-colors flex items-center justify-center">
                    <span className="mr-3">💬</span> Iniciar proceso de compra
                  </button>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button onClick={() => navigate('/carrito')} className="w-full py-3 px-6 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700">Volver al carrito</button>
                  <button onClick={() => navigate('/')} className="w-full py-3 px-6 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300">Continuar comprando</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // CART DETAIL VIEW (public)
  // ============================================
  const CartDetailView = () => {
    const { cartId: viewCartId } = useParams();
    const [cartData, setCartData] = useState(null as any);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      // Si es operador autenticado, redirigir a la consola operativa
      // ANTES de ejecutar cualquier lógica de la vista pública
      // (evita api.getCart + socketClient.joinCart del namespace cliente)
      if (operatorMode && viewCartId) {
        navigate('/admin/cart/' + viewCartId, { replace: true });
        return;
      }
      if (!viewCartId) { setLoading(false); return; }
      socketClient.joinCart(viewCartId);
  
      api.getCart(viewCartId).then(data => {
        if (data.success && data.cart) {
          const enriched = {
            ...data.cart,
            items: (data.cart.items || []).map(item => {
              const product = products.find(p => p.code === item.productCode);
              return { ...item, product: product || null };
            })
          };
          setCartData(enriched);
        } else setCartData(null);
      }).catch(() => setCartData(null)).finally(() => setLoading(false));
      return () => { socketClient.leaveCart(viewCartId); };
    }, [viewCartId, operatorMode, navigate]);

    if (!viewCartId) return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <div className="text-4xl mb-4">❌</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">ID de carrito no especificado</h1>
        <button onClick={() => navigate('/')} className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium">Volver al inicio</button>
      </div>
    );

    if (loading) return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <div className="text-4xl mb-4">⏳</div>
        <div className="text-xl font-medium text-gray-700">Cargando carrito...</div>
      </div>
    );

    if (!cartData) return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <div className="text-4xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Carrito no encontrado</h1>
        <p className="text-gray-600 mb-6">El carrito ' + viewCartId + ' no existe o ha expirado.</p>
        <button onClick={() => navigate('/')} className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium">Volver a la tienda</button>
      </div>
    );

    const totalPrice = cartData.items.reduce((sum, item) => sum + (item.price || 0), 0);
    const statusLabels = { pending: 'Pendiente', verifying: 'En verificación de pago', sold: 'Vendido', active: 'Liberado' };
    const statusStyles = { pending: 'bg-yellow-100 text-yellow-800', verifying: 'bg-blue-100 text-blue-800', sold: 'bg-green-100 text-green-800', active: 'bg-gray-100 text-gray-500' };

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/')} className="text-purple-600 hover:text-purple-800 font-medium mb-2 block">← Volver a la tienda</button>
            <h1 className="text-3xl font-bold text-gray-900">Carrito {viewCartId}</h1>
            <p className="text-gray-600 text-sm">{cartData.items.length} producto(s) • Creado {new Date(cartData.createdAt || cartData.created_at).toLocaleString()}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {cartData.items.map((item) => (
              <div key={item.productCode} className="flex items-center p-4 hover:bg-gray-50 transition-colors">
                <div className="w-16 h-16 mr-4 flex-shrink-0 product-media">
                  <img src={item.product?.images?.[0] || '/logos/logo-kimberly-clean.png'} alt={item.name} className="w-full h-full rounded-lg" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{item.name}</div>
                  <div className="text-sm text-gray-500">{item.productCode}</div>
                </div>
                <div className="text-right mr-4">
                  <div className="text-lg font-bold text-gray-900">RD$ {(item.price || 0).toLocaleString()}</div>
                </div>
                <span className={'px-3 py-1 rounded-full text-xs font-medium ' + (statusStyles[item.status] || 'bg-gray-100 text-gray-800')}>
                  {statusLabels[item.status] || item.status}
                </span>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-gray-700">Total</span>
              <span className="text-2xl font-bold text-purple-600">RD$ {totalPrice.toLocaleString()}</span>
            </div>
          </div>
        </div>
        {cartData.status === 'completed' && (
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
            <div className="text-gray-500 font-medium">✅ Este carrito ha sido completado</div>
          </div>
        )}
        {cartData.status === 'expired' && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-center">
            <div className="text-red-600 font-bold mb-2">🔒 Carrito {viewCartId} expirado</div>
            <div className="text-red-700 text-sm">
              Los artículos fueron liberados.<br />
              Revisar disponibilidad y contactar al cliente.
            </div>
          </div>
        )}
        {/* Extra padding to avoid cutoff when reaching bottom */}
        <div className="h-16"></div>
      </div>
    );
  };

  // ============================================
  // ADMIN CART VIEW (Operator Console)
  // ============================================
  const AdminCartView = () => {
    const { cartId: adminCartId } = useParams();
    const [savedAdminId, setSavedAdminId] = useState(null as any);

    useEffect(() => {
      if (adminCartId) {
        localStorage.setItem('kf25_admin_last_cart', adminCartId);
        setSavedAdminId(adminCartId);
        setOperatorActiveCart(adminCartId);
        console.log('[Admin] Viewing cart: ' + adminCartId);

        // Connect operator socket
        const token = sessionStorage.getItem('kf25_op_token');
        if (token) {
          socketOperator.connect(token);
          // MP-STAB-03 — si el socket rechaza la sesión, misma rutina global única
          socketOperator.onAuthError(() => api.handleSessionExpired('reauth_required'));
          socketOperator.joinCart(adminCartId);
        }

        return () => {
          socketOperator.leaveCart(adminCartId);
        };
      }
    }, [adminCartId]);

    const handleGoToStore = useCallback(() => {
      navigate('/');
    }, []);

    const handleBackToDashboard = useCallback(() => {
      navigate('/admin');
    }, []);

    if (!adminCartId) {
      return (
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <div className="text-4xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No se especificó carrito</h1>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium">Volver al inicio</button>
        </div>
      );
    }

    return (
      <div className={adminCartId ? '' : 'hidden'}>
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="mb-4 flex items-center justify-between">
            <button onClick={handleBackToDashboard}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm">
              ← Panel operadora
            </button>
            <button onClick={handleGoToStore}
              className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium text-sm">
              Tienda →
            </button>
            <button onClick={handleLogout}
              className="ml-auto px-4 py-2 bg-red-100 border border-red-200 shadow-sm hover:bg-red-200 text-red-700 rounded-lg font-medium text-sm">
              Cerrar sesión
            </button>
          </div>
        </div>
        <OperatorConsole
          cartId={adminCartId}
          onBack={handleBackToDashboard}
          onGoToStore={handleGoToStore}
          hideNavHeader
          onProductsUpdated={refreshProducts}
        />
      </div>
    );
  };

  // ============================================
  // ADMIN OP VIEW
  // ============================================
    const AdminOpView = () => {
    const { productCode } = useParams();
    const [product, setProduct] = useState(null as any);
    const [loading, setLoading] = useState(true);
    // UX-001E modal: editing=true abre el MISMO formulario (UploadConsole) como
    // modal — sin navegación; refreshTick re-ejecuta la carga al cerrar.
    const [editing, setEditing] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);
    // PLATFORM-UX-001: mientras el modal de edición esté abierto, la app está
    // ocupada → el refresh de versión se difiere (busyRegistry).
    useEffect(() => {
      if (editing) {
        busyRegistry.acquire('edit-modal');
        return () => busyRegistry.release('edit-modal');
      }
    }, [editing]);
    const [addMediaState, setAddMediaState] = useState('idle');
    const [mediaAccept, setMediaAccept] = useState({ img: 'image/*', vid: 'video/*' });

    // Capacidades de media derivadas del registro (backend) — fallback genérico
    useEffect(() => {
      api.fetchMediaLimits()
        .then((data) => {
          if (data && data.limits) {
            setMediaAccept({
              img: (data.limits.allowedImageTypes || []).join(',') || 'image/*',
              vid: (data.limits.allowedVideoTypes || []).join(',') || 'video/*'
            });
          }
        })
        .catch(() => {});
    }, []);
    const [addMediaError, setAddMediaError] = useState(null as any);
    const [mediaVersion, setMediaVersion] = useState(0);
    // PLATFORM-UX-001: subida de media en curso = ocupado
    useEffect(() => {
      if (addMediaState === 'uploading') {
        busyRegistry.acquire('media-upload');
        return () => busyRegistry.release('media-upload');
      }
    }, [addMediaState]);

    useEffect(() => {
      if (productCode) {
        setOperatorActiveCart('op_' + productCode);
        api.listProducts().then(data => {
          if (data && data.products) {
            const found = data.products.find(p => p.code === productCode.toUpperCase());
            setProduct(found || null);
          }
          setLoading(false);
        }).catch(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }, [productCode, mediaVersion, refreshTick]);

    // Realtime Platform: el detalle de producto del operador consume el MISMO
    // motor — product:changed actualiza el producto sin refetch ni polling.
    useEffect(() => {
      if (!productCode) return;
      const code = productCode.toUpperCase();
      const unsub = socketClient.on('product:changed', (data: any) => {
        if (data && data.entityId === code) {
          setProduct(prev => (prev && prev.code === code ? { ...prev, ...data.current } : prev));
        }
      });
      const unsubResync = socketClient.onResync(() => {
        api.listProducts().then(data => {
          if (data && data.products) {
            const found = data.products.find(p => p.code === code);
            if (found) setProduct(found);
          }
        }).catch(() => {});
      });
      return () => { unsub(); unsubResync(); };
    }, [productCode]);

    const handleAddMedia = async (e) => {
      const file = e.target.files?.[0];
      if (!file || !product) return;

      setAddMediaState('uploading');
      setAddMediaError(null);

      try {
        const formData = new FormData();
        formData.append('media', file);
        await api.addProductMedia(product.code, formData);
        setAddMediaState('success');
        setMediaVersion(v => v + 1);
      } catch (err) {
        setAddMediaError(err?.message || 'Error al agregar archivo.');
        setAddMediaState('error');
      }

      e.target.value = '';
    };

    if (!productCode) {
      return (
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <div className="text-4xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No se especificó producto</h1>
          <button onClick={() => navigate('/admin')} className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium">Volver a la consola</button>
        </div>
      );
    }

    if (loading) {
      return <div className="max-w-6xl mx-auto px-4 py-8 text-center text-gray-500">Cargando producto...</div>;
    }

    const virtualCart = product ? {
      items: [{
        productCode: product.code,
        name: product.name,
        price: product.price || 0,
        status: product.manualBlockedBy ? 'pending' : product.status,
        image: product.images?.[0] || null
      }]
    } : null;

    return (
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm">
            ← Panel operadora
          </button>
          <button onClick={() => navigate('/')}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium text-sm">
            Tienda →
          </button>
          <button onClick={handleLogout}
            className="ml-auto px-4 py-2 text-red-500 hover:text-red-700 font-medium text-sm">
            Cerrar sesión
          </button>
        </div>
        {/* KF25-UX-001A: media upload movido FUERA de la cabecera (era un bloque
            inline dentro de la fila flex del header) — sección propia, jerarquía
            visual correcta. Misma funcionalidad, mismo componente. */}
        {product && (
          <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Media del producto</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">{product.media?.length || product.images?.length || 0} archivo(s)</span>
                {/* UX-001E: entrada de edición — MISMO formulario (UploadConsole) en Edit Mode.
                    Flujo preferido: modal sobre la consola (sin navegación — el operador
                    permanece en la misma pantalla; el contexto UX-001D ni siquiera se toca). */}
                <button
                  onClick={() => setEditing(true)}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-xs transition-colors"
                >
                  ✏️ Editar
                </button>
              </div>
            </div>
            {/* UX-001E (revisión final #4): identidad del producto visible en la
                consola — tras cerrar el modal de edición, la info actualizada
                (nombre/precio/categoría/descripción) se ve de inmediato. */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm font-semibold text-gray-800">{product.name || product.code}</p>
              <p className="text-xs text-gray-500">RD$ {(product.price || 0).toLocaleString()}{product.category ? ' • ' + product.category : ''}</p>
              {product.description && (
                <p className="text-xs text-gray-400 mt-1 line-clamp-2">{product.description}</p>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              {/* + Foto */}
              <div className="relative inline-block">
                <input
                  type="file"
                  accept={mediaAccept.img}
                  onChange={handleAddMedia}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  style={{ fontSize: 0 }}
                />
                <div className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 pointer-events-none font-medium">
                  {addMediaState === 'uploading' ? '⏳ Subiendo...' : '📷 + Foto'}
                </div>
              </div>
              {/* + Video */}
              <div className="relative inline-block">
                <input
                  type="file"
                  accept={mediaAccept.vid}
                  onChange={handleAddMedia}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  style={{ fontSize: 0 }}
                />
                <div className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 pointer-events-none font-medium">
                  {addMediaState === 'uploading' ? '⏳ Subiendo...' : '🎬 + Video'}
                </div>
              </div>
            </div>
            {addMediaState === 'success' && (
              <p className="text-green-600 text-sm mt-2">✅ Archivo agregado correctamente</p>
            )}
            {addMediaState === 'error' && (
              <p className="text-red-500 text-sm mt-2">❌ {addMediaError}</p>
            )}
          </div>
        )}

        {virtualCart ? (
          <OperatorConsole
            cartId={'op_' + productCode}
            onBack={() => navigate('/admin')}
            onGoToStore={() => navigate('/')}
            hideNavHeader
            virtualCart={virtualCart}
            onProductsUpdated={refreshProducts}
          />
        ) : (
          <div className="text-center py-12 text-gray-500">Producto no encontrado</div>
        )}

        {/* UX-001E modal: el MISMO formulario (UploadConsole) sobre la consola —
            sin navegación, el operador permanece en la misma pantalla. Cerrar:
            ✕ o clic en el backdrop. Al guardar (onSuccess) se cierra y se
            refresca el producto (refreshTick → re-carga). */}
        {editing && product && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4"
            onClick={(e) => { if (e.target === e.currentTarget) setEditing(false); }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl relative">
              <button
                onClick={() => setEditing(false)}
                aria-label="Cerrar edición"
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 text-lg leading-none flex items-center justify-center transition-colors"
              >✕</button>
              <UploadConsole
                editCode={product.code}
                onProductsUpdated={refreshProducts}
                onSuccess={() => { setEditing(false); setRefreshTick(t => t + 1); }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // CHECKOUT QUEUE
  // ============================================
  const CheckoutQueueView = () => {
    const [checkoutCarts, setCheckoutCarts] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const loadQueue = () => {
      api.getDashboard({ scope: 'checkout' })
        .then(data => {
          if (data.success) {
            const queue = (data.activeCarts || [])
              .sort((a, b) => {
                if (a.timerPhase === 'checkout_sent' && b.timerPhase !== 'checkout_sent') return -1;
                if (a.timerPhase !== 'checkout_sent' && b.timerPhase === 'checkout_sent') return 1;
                return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
              });
            setCheckoutCarts(queue);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    };

    useEffect(() => {
      loadQueue();
      // Realtime Platform: la cola es socket-driven (mismo motor que la Tienda).
      // Sin polling. Recuperación tras reconexión vía onResync.
      const refreshEvents = ['cart:created', 'item:added', 'item:removed', 'payment:verifying', 'item:sold', 'item:released', 'cart:closed', 'timer:started', 'client:warning', 'timer:expired'];
      const unsubs = refreshEvents.map(evt => socketOperator.on(evt, loadQueue));
      const unsubResync = socketOperator.onResync(loadQueue);
      return () => { unsubs.forEach(u => u()); unsubResync(); };
    }, []);

    const pendingCarts = checkoutCarts.filter(c => c.timerPhase === 'checkout_sent');
    const processCarts = checkoutCarts.filter(c => c.timerPhase === 'operator_active');

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* KF25-UX-001A: cabecera canónica única (mismo patrón que el resto de
            pantallas operativas): Panel / Tienda / Cerrar sesión en una fila. */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex gap-2">
            <button onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm">
              ← Panel operadora
            </button>
            <button onClick={() => navigate('/')}
              className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium text-sm">
              Tienda →
            </button>
          </div>
          <button onClick={handleLogout}
            className="px-4 py-2 text-red-500 hover:text-red-700 font-medium text-sm">
            Cerrar sesión
          </button>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">📩 Checkout Console</h1>
        <p className="text-gray-600 mt-1 mb-4">Carritos en proceso de compra humana</p>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : checkoutCarts.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-xl font-semibold text-gray-700">No hay carritos en checkout</h3>
            <p className="text-gray-500 mt-2">Todos los pedidos han sido procesados</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pendingCarts.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-red-700 flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>
                  Pendientes de atención ({pendingCarts.length})
                </h2>
                <div className="space-y-3">
                  {pendingCarts.map(cart => (
                    <div key={cart.id} onClick={() => navigate('/admin/cart/' + cart.id)}
                      className="p-4 border-2 border-red-300 bg-red-50 rounded-xl cursor-pointer hover:bg-red-100 hover:shadow-md transition-all">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-bold text-gray-900 text-lg">{cart.id}</div>
                          <div className="text-sm text-gray-600">
                            {cart.clientName || 'Sin nombre'}{cart.clientPhone ? ' • ' + cart.clientPhone : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">{cart.items} {cart.items === 1 ? 'producto' : 'productos'}</div>
                          <span className="inline-block mt-1 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-300">🔴 Sin atender</span>
                        </div>
                      </div>
                      <div className="mt-2 text-center">
                        <span className="text-sm font-medium text-red-600 hover:text-red-800">✅ Tomar carrito →</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {processCarts.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-green-700 flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
                  En proceso ({processCarts.length})
                </h2>
                <div className="space-y-3">
                  {processCarts.map(cart => (
                    <div key={cart.id} onClick={() => navigate('/admin/cart/' + cart.id)}
                      className="p-4 border-2 border-green-300 bg-green-50 rounded-xl cursor-pointer hover:bg-green-100 hover:shadow-md transition-all">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-bold text-gray-900 text-lg">{cart.id}</div>
                          <div className="text-sm text-gray-600">
                            {cart.clientName || 'Sin nombre'}{cart.clientPhone ? ' • ' + cart.clientPhone : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">{cart.items} {cart.items === 1 ? 'producto' : 'productos'}</div>
                          <span className="inline-block mt-1 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-300">🟢 En proceso</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // ADMIN DASHBOARD
  // ============================================
  const AdminDashboard = () => {
    const [activeCarts, setActiveCarts] = useState([]);
    const [loading, setLoading] = useState(true);
    // UX-001D: búsqueda y bloqueos en curso sobreviven a la navegación operativa
    // (consola de producto/carrito, upload, checkout-queue). Los RESULTADOS no se
    // persisten (tamaño) — se re-ejecuta la búsqueda al volver (1 llamada API).
    const [searchQ, setSearchQ] = useViewMemory('dash.search', '');
    const [searchResults, setSearchResults] = useState(null as any);
    const [blockedSearchProducts, setBlockedSearchProducts] = useViewMemory('dash.blocked', [] as string[]);
    // UX-001D: la vista está "lista" cuando browse cargó o la búsqueda re-ejecutada
    // terminó → SOLO entonces se restaura el scroll (posición de trabajo exacta).
    const [viewReady, setViewReady] = useState(false);
    const restoredSearchRef = useRef(false);
    useEffect(() => {
      if (restoredSearchRef.current) return;
      restoredSearchRef.current = true;
      if (searchQ.trim()) handleSearch();
    }, []);
    useRestoreScroll('dash', viewReady);

    useEffect(() => {
      const loadDashboard = () => {
        api.getDashboard({ scope: 'browse' })
          .then(data => {
            if (data.success) {
              setActiveCarts(data.activeCarts || []);
            }
            if (!searchQ.trim()) setViewReady(true); // browse listo (sin búsqueda guardada)
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      };

      loadDashboard();

      // Realtime Platform: dashboard socket-driven (mismo motor). Sin polling.
      const refreshEvents = ['cart:created', 'item:added', 'item:removed', 'payment:verifying', 'item:sold', 'item:released', 'cart:closed', 'timer:started', 'client:warning', 'timer:expired', 'operator:opened-cart'];
      const unsubs = refreshEvents.map(evt => socketOperator.on(evt, loadDashboard));
      const unsubResync = socketOperator.onResync(loadDashboard);
      return () => { unsubs.forEach(u => u()); unsubResync(); };
    }, []);

    const handleSearch = async () => {
      if (!searchQ.trim()) return;
      try {
        const data = await api.searchOperator(searchQ.trim());
        if (data.success) {
          setSearchResults(data);
          setViewReady(true); // UX-001D: resultados reconstruidos → restaurar scroll
        }
      } catch (e) {
        console.error('[Admin] Search error:', e);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') handleSearch();
    };

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-purple-100 border border-purple-200 shadow-sm hover:bg-purple-200 text-purple-800 rounded-lg font-medium text-sm">← Ir a tienda</button>
          <button onClick={handleLogout} className="px-4 py-2 bg-red-100 border border-red-200 shadow-sm hover:bg-red-200 text-red-700 rounded-lg font-medium text-sm">Cerrar sesión</button>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">🛠️ Panel de operadora</h1>
        <p className="text-gray-600 mt-1">Administración de carritos y ventas</p>
        <div className="mt-3 flex gap-2">
          <button onClick={() => navigate('/admin/checkout-queue')} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium text-sm inline-block">
            📩 Checkout Console
          </button>
          <button onClick={() => navigate('/admin/upload')} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm inline-block">
            📤 Subir producto
          </button>
          <button onClick={() => navigate('/admin/devices')} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm inline-block">
            📱 Dispositivos
          </button>
        </div>

        {/* Search */}
        <div className="mb-6 bg-white rounded-xl shadow border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Buscar carrito o producto</label>
          <div className="flex gap-3">
            <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Ej: CT-031, KF25-017, Blusa, +18095550100"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
            <button onClick={handleSearch}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Buscar</button>
          </div>
          {searchResults && searchResults.results && searchResults.results.length > 0 && (
            <div className="mt-4 space-y-2">
              {(() => {
                // Agrupar: carritos independientes, productos en carrito, productos independientes
                const cartResults = [];
                const standaloneProducts = [];
                const cartProductMap = {}; // cartId → [{productCode, name, image, status}, ...]

                searchResults.results.forEach(item => {
                  if (item.type === 'cart') {
                    cartResults.push({ ...item, subItems: [] });
                  } else if (item.type === 'product') {
                    if (item.currentCart) {
                      // Producto dentro de un carrito — agrupar
                      if (!cartProductMap[item.currentCart]) {
                        cartProductMap[item.currentCart] = [];
                      }
                      cartProductMap[item.currentCart].push({
                        productCode: item.productCode,
                        name: item.name,
                        image: item.image,
                        status: item.status
                      });
                    } else {
                      // Producto independiente
                      standaloneProducts.push(item);
                    }
                  }
                });

                // Asignar sub-items a carritos existentes
                cartResults.forEach(cart => {
                  if (cartProductMap[cart.cartId]) {
                    cart.subItems = cartProductMap[cart.cartId];
                  }
                });

                // Carritos mencionados solo por productos (sin entrada propia en results)
                const orphanCartIds = Object.keys(cartProductMap).filter(
                  cartId => !cartResults.some(c => c.cartId === cartId)
                );

                return (
                  <>
                    {/* Carritos con sub-items */}
                    {cartResults.map(cart => {
                      // Cart from search (or fullData from product match)
                      const subItems = cart.subItems.length > 0
                        ? cart.subItems
                        : (cart.fullData ? (cart.cartItems || []) : []);
                      const totalItems = subItems.length > 0
                        ? subItems.length
                        : (cart.items || 0);
                      return (
                        <div key={cart.cartId} onClick={() => navigate('/admin/cart/' + cart.cartId)}
                          className="p-3 border border-purple-200 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-medium">{cart.cartId}</span>
                              <span className="text-xs text-gray-400 ml-2">{cart.clientName || 'Sin nombre'}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-600">
                                {totalItems} {totalItems === 1 ? 'producto' : 'productos'}
                              </div>
                            </div>
                          </div>
                          {subItems.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {subItems.map(sub => (
                                <div key={sub.productCode}
                                  className="flex items-center gap-2 pl-2 py-1 border-l-2 border-purple-300 bg-purple-50/50 rounded-r">
                                  <div className="w-7 h-7 rounded bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center product-media">
                                    {sub.image ? (
                                      <img src={sub.image} alt={sub.name} className="w-full h-full" />
                                    ) : (
                                      <span className="text-xs">📷</span>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-gray-700 truncate">{sub.name}</div>
                                    <div className="text-xs text-gray-400">{sub.productCode}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Carritos huérfanos (solo mencionados por productos, sin entrada en results) */}
                    {orphanCartIds.map(cartId => (
                      <div key={cartId} onClick={() => navigate('/admin/cart/' + cartId)}
                        className="p-3 border border-yellow-200 rounded-lg hover:bg-yellow-50 cursor-pointer transition-colors">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{cartId}</span>
                          <div className="text-sm text-yellow-700">
                            {cartProductMap[cartId].length} producto(s)
                          </div>
                        </div>
                        <div className="mt-2 space-y-1.5">
                          {cartProductMap[cartId].map(sub => (
                            <div key={sub.productCode}
                              className="flex items-center gap-2 pl-2 py-1 border-l-2 border-yellow-300 bg-yellow-50/50 rounded-r">
                              <div className="w-7 h-7 rounded bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center product-media">
                                {sub.image ? (
                                  <img src={sub.image} alt={sub.name} className="w-full h-full" />
                                ) : (
                                  <span className="text-xs">📷</span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-700 truncate">{sub.name}</div>
                                <div className="text-xs text-gray-400">{sub.productCode}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Productos independientes (sin carrito) */}
                    {standaloneProducts.map(item => {
                      const isBlocked = blockedSearchProducts.includes(item.productCode);
                      const handleBlock = async (e: React.MouseEvent) => {
                        e.stopPropagation();
                        const res = await api.blockProduct(item.productCode);
                        if (res.success) {
                          // UX-001D: la persistencia es síncrona en el setter de
                          // useViewMemory (ref-mirror) — sobrevive a la navegación
                          // del mismo batch aunque React descarte el updater.
                          setBlockedSearchProducts(prev => [...prev, item.productCode]);
                          setBlockedGlobal(prev => [...prev, item.productCode]);
                          navigate('/admin/op/' + item.productCode);
                          refreshProducts();
                        }
                      };
                      const handleUnblock = async (e: React.MouseEvent) => {
                        e.stopPropagation();
                        const res = await api.unblockProduct(item.productCode);
                        if (res.success) {
                          setBlockedSearchProducts(prev => prev.filter(c => c !== item.productCode));
                          setBlockedGlobal(prev => prev.filter(c => c !== item.productCode));
                          refreshProducts();
                        }
                      };
                      if (isBlocked) {
                        return (
                          <div key={item.productCode}
                            className="p-3 border border-yellow-300 rounded-lg bg-yellow-50 flex items-center gap-3 transition-colors">
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center product-media">
                              {item.image ? (
                                <img src={item.image} alt={item.name} className="w-full h-full" />
                              ) : (
                                <span className="text-lg">📷</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{item.name}</div>
                              <div className="text-xs text-gray-500">{item.productCode}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium border bg-yellow-100 text-yellow-700 border-yellow-200">
                                Bloqueado
                              </span>
                              <button onClick={handleUnblock}
                                className="px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                Liberar
                              </button>
                            </div>
                          </div>
                        );
                      }
                      const badgeColor = {
                        active: 'bg-green-100 text-green-700 border-green-200',
                        in_process: 'bg-purple-100 text-purple-700 border-purple-200',
                        verifying: 'bg-blue-100 text-blue-700 border-blue-200',
                        sold: 'bg-gray-100 text-gray-500 border-gray-200'
                      }[item.status] || 'bg-gray-100 text-gray-500 border-gray-200';
                      const badgeText = {
                        active: 'Disponible',
                        in_process: 'En proceso',
                        verifying: 'Verificando',
                        sold: 'Vendido'
                      }[item.status] || item.status;
                      return (
                        <div key={item.productCode} onClick={handleBlock}
                          className="p-3 border border-purple-200 rounded-lg hover:bg-purple-50 cursor-pointer flex items-center gap-3 transition-colors">
                          <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center product-media">
                            {item.image ? (
                              <img src={item.image} alt={item.name} className="w-full h-full" />
                            ) : (
                              <span className="text-lg">📷</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.productCode}</div>
                          </div>
                          <div className="text-right flex-shrink-0 flex items-center gap-2">
                            <button onClick={async (e) => {
                              e.stopPropagation();
                              const res = await api.hideProduct(item.productCode);
                              if (res.success) {
                                setSearchResults(prev => ({
                                  ...prev,
                                  results: (prev.results || []).filter(r =>
                                    r.productCode !== item.productCode
                                  ),
                                  count: (prev.count || 1) - 1
                                }));
                              }
                            }}
                              className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 bg-gray-50 hover:bg-gray-100 rounded transition-colors"
                              title="Ocultar del feed público">
                              ◐
                            </button>
                            <span className={"inline-block px-2 py-0.5 rounded-full text-xs font-medium border " + badgeColor}>
                              {badgeText}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Active carts */}
        <div className="bg-white rounded-xl shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Carritos activos ({loading ? '...' : activeCarts.length})
            </h2>
          </div>
          {loading ? (
            <div className="p-12 text-center text-gray-500">Cargando...</div>
          ) : activeCarts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No hay carritos activos</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {activeCarts.map(cart => (
                <div key={cart.id} onClick={() => navigate('/admin/cart/' + cart.id)}
                  className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center">
                  <div>
                    <div className="font-medium">{cart.id}</div>
                    <div className="text-sm text-gray-500">{cart.clientName || 'Sin nombre'} {cart.clientPhone ? '• ' + cart.clientPhone : ''}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">{cart.items} producto(s)</div>
                    <div className="text-sm font-medium text-purple-600">
                      {cart.timer?.phase === 'operator_active' ? '🛠️ Activo' :
                       cart.timer?.phase === 'browsing' ? 'Navegando' :
                       cart.timer?.phase === 'checkout_sent' ? '⏳ En espera' :
                       cart.timer?.phase === 'expired' ? '⌛ Expirado' : cart.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // ADAPTER — bridges useParams + Route to ProductView
  // ============================================
  const ProductDetailAdapter = () => {
    const { productCode } = useParams();
    return <ProductView productCode={productCode} />;
  };

  // ============================================
  // ROUTER — wrap everything in Routes
  // ============================================
  return (
    <>
    <Routes>
      <Route path="/" element={<HomeView />} />
      <Route path="/producto/:productCode" element={<ProductDetailAdapter />} />
      <Route path="/carrito" element={<CartView />} />
      <Route path="/checkout" element={<CheckoutView />} />
      <Route path="/cart/:cartId" element={<CartDetailView />} />
      <Route path="/admin" element={authority.state === 'resolving' ? <AuthorityLoadingScreen /> : operatorMode ? (isBootstrapSession ? <Navigate to="/admin/devices" replace /> : <AdminDashboard />) : <DeviceAuthScreen reason={authority.reason} />} />
      <Route path="/admin/devices" element={authority.state === 'resolving' ? <AuthorityLoadingScreen /> : operatorMode ? <DevicesAdminView bootstrap={isBootstrapSession} /> : <DeviceAuthScreen reason={authority.reason} />} />
      <Route path="/admin/op/:productCode" element={authority.state === 'resolving' ? <AuthorityLoadingScreen /> : operatorMode ? (isBootstrapSession ? <Navigate to="/admin/devices" replace /> : <AdminOpView />) : <DeviceAuthScreen reason={authority.reason} />} />
      <Route path="/admin/cart/:cartId" element={authority.state === 'resolving' ? <AuthorityLoadingScreen /> : operatorMode ? (isBootstrapSession ? <Navigate to="/admin/devices" replace /> : <AdminCartView />) : <DeviceAuthScreen reason={authority.reason} />} />
      <Route path="/admin/checkout-queue" element={authority.state === 'resolving' ? <AuthorityLoadingScreen /> : operatorMode ? (isBootstrapSession ? <Navigate to="/admin/devices" replace /> : <CheckoutQueueView />) : <DeviceAuthScreen reason={authority.reason} />} />
      <Route path="/admin/upload" element={authority.state === 'resolving' ? <AuthorityLoadingScreen /> : operatorMode ? (isBootstrapSession ? <Navigate to="/admin/devices" replace /> : <UploadRoute onUpdated={refreshProducts} />) : <DeviceAuthScreen reason={authority.reason} />} />      {/* KF25-AUTH-01/02 — ruta EXPLÍCITA de recuperación (contraseña para re-registro). Nunca automática. */}
      <Route path="/admin/recovery" element={<RecoveryLogin />} />
      <Route path="/admin/cart" element={<Navigate to="/admin" replace />} />
      <Route path="/enroll/:id" element={<EnrollPage />} />   // MP-STAB-04 — pública: el link ES la credencial
      <Route path="*" element={<NotFoundView />} />
    </Routes>
      {/* PLATFORM-UX-001: banner de version - operador notificado; tienda silenciosa */}
      <VersionBanner mode={operatorMode ? 'operator' : 'store'} />
    </>
  );
}

// ============================================
// NOT FOUND VIEW
// ============================================
function NotFoundView() {
  const navigate = useNavigate();
  return (
    <div className="max-w-6xl mx-auto px-4 py-12 text-center">
      <div className="text-6xl mb-4">🔍</div>
      <h1 className="text-3xl font-bold text-gray-900 mb-4">Página no encontrada</h1>
      <p className="text-gray-600 mb-8">La página que buscas no existe.</p>
      <button onClick={() => navigate('/')} className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium">
        ← Volver a la tienda
      </button>
    </div>
  );
}

// ============================================
// APP ROOT — wraps with Router
// ============================================
function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <AppContent />
      </div>
    </Router>
  );
}

export default App;
